import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Input,
  Toggle,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import {
  TabContent,
  FormSection,
  FormRow,
  FormActions,
  QuotaGrid,
} from "./styles";

type NetworkQuotasTabProps = {
  projectId: string;
  regionId: string;
};

const NetworkQuotasTab: FC<NetworkQuotasTabProps> = ({ projectId, regionId }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const [loading, setLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    floatingip: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    network: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    port: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    rbac_policy: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    router: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    security_group: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    security_group_rule: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    subnet: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
    subnetpool: yup.number().min(-1, t("INVALID_VALUE")).required(t("REQUIRED_FIELD")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      floatingip: -1,
      network: -1,
      port: -1,
      rbac_policy: -1,
      router: -1,
      security_group: -1,
      security_group_rule: -1,
      subnet: -1,
      subnetpool: -1,
      force: false,
      set_for_all_regions: false,
    },
    onSubmit: (formValues) => {
      setUpdateLoading(true);

      const payload = {
        ...formValues,
        region_id: regionId,
      };

      projectService
        .setNetworkQuotas(projectId, payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("QUOTAS_UPDATED_SUCCESSFULLY"),
          });
          loadQuotas();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_UPDATE_QUOTAS");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setUpdateLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
  } = formik;

  const loadQuotas = useCallback(() => {
    setLoading(true);
    projectService
      .getNetworkQuotas(projectId, regionId)
      .then((res) => {
        const quotas = res.data;
        setValues({
          floatingip: quotas.floatingip ?? -1,
          network: quotas.network ?? -1,
          port: quotas.port ?? -1,
          rbac_policy: quotas.rbac_policy ?? -1,
          router: quotas.router ?? -1,
          security_group: quotas.security_group ?? -1,
          security_group_rule: quotas.security_group_rule ?? -1,
          subnet: quotas.subnet ?? -1,
          subnetpool: quotas.subnetpool ?? -1,
          force: false,
          set_for_all_regions: false,
        });
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_QUOTAS");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId, regionId, setValues, t, viewAlert]);

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  if (loading) {
    return (
      <TabContent>
        <p>{t("LOADING")}</p>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <QuotaGrid>
          <FormRow>
            <Input
              required
              title={t("FLOATING_IPS")}
              placeholder="-1"
              fieldName="floatingip"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("NETWORKS")}
              placeholder="-1"
              fieldName="network"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("PORTS")}
              placeholder="-1"
              fieldName="port"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("RBAC_POLICIES")}
              placeholder="-1"
              fieldName="rbac_policy"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("ROUTERS")}
              placeholder="-1"
              fieldName="router"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SECURITY_GROUPS")}
              placeholder="-1"
              fieldName="security_group"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SECURITY_GROUP_RULES")}
              placeholder="-1"
              fieldName="security_group_rule"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SUBNETS")}
              placeholder="-1"
              fieldName="subnet"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>

          <FormRow>
            <Input
              required
              title={t("SUBNET_POOLS")}
              placeholder="-1"
              fieldName="subnetpool"
              type="number"
              toolTip={true}
              toolTipText={t("QUOTA_UNLIMITED_HINT")}
              {...formikProps}
            />
          </FormRow>
        </QuotaGrid>

        <FormRow>
          <Toggle
            value={values.force}
            onChange={() => setFieldValue("force", !values.force)}
            title={t("FORCE_UPDATE")}
            toolTip={true}
            toolTipText={t("FORCE_UPDATE_HINT")}
          />
        </FormRow>

        <FormRow>
          <Toggle
            value={values.set_for_all_regions}
            onChange={() => setFieldValue("set_for_all_regions", !values.set_for_all_regions)}
            title={t("SET_FOR_ALL_REGIONS")}
            toolTip={true}
            toolTipText={t("SET_FOR_ALL_REGIONS_HINT")}
          />
        </FormRow>

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            title={t("UPDATE_QUOTAS")}
            disabled={updateLoading}
            loading={updateLoading}
          />
        </FormActions>
      </FormSection>
    </TabContent>
  );
};

export default NetworkQuotasTab;
